import { createTheme, Theme } from '@mui/material/styles';

import { paletteFor, radius, ThemeMode } from './styles/tokens';

// MUI theme derived from the REMUS design tokens (frontend/src/styles/tokens.ts
// and the matching CSS variables in frontend/src/styles/design-system.less).
//
// No hex is declared here — change the brand by editing tokens.ts only.
//
// `createAppTheme(mode)` builds the palette from a concrete token set
// (MUI needs real hex to derive contrast/ripple/disabled shades — it can't
// resolve `var(...)` at construction time). The component styleOverrides
// below instead reference `var(--ds-*)` so chrome (dialog/accordion fills,
// borders, elevation shadows) flips with the active theme automatically.
export const createAppTheme = (mode: ThemeMode = 'light'): Theme => {
  const ds = paletteFor(mode);
  return createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? ds.accent : ds.brandNavy,
      light: ds.brandNavySoft,
      dark: ds.brandNavyDeep,
      contrastText: ds.onAccent,
    },
    secondary: {
      main: ds.muted,
    },
    success: {
      main: ds.successMain,
      dark: ds.successDark,
      light: ds.successLight,
      contrastText: ds.onAccent,
    },
    error: {
      main: ds.dangerMain,
      dark: ds.danger,
      light: ds.dangerLight,
      contrastText: ds.onAccent,
    },
    warning: {
      main: ds.warningMain,
      dark: ds.warningDark,
      light: ds.warningLight,
      contrastText: ds.onAccent,
    },
    info: {
      main: ds.infoMain,
      dark: ds.infoDark,
      light: ds.infoLight,
      contrastText: ds.onAccent,
    },
    text: {
      primary: ds.text,
      secondary: ds.muted,
      disabled: ds.disabled,
    },
    divider: ds.border,
    background: {
      default: ds.bg,
      paper: ds.surface,
    },
  },
  shape: {
    borderRadius: radius.md,
  },
  typography: {
    fontFamily:
      '"Nunito Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontSize: '2rem', fontWeight: 600 },
    h2: { fontSize: '1.5rem', fontWeight: 600 },
    h3: { fontSize: '1.25rem', fontWeight: 600 },
    h4: { fontSize: '1.125rem', fontWeight: 600 },
    h5: { fontSize: '1rem', fontWeight: 600 },
    h6: { fontSize: '0.875rem', fontWeight: 600 },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'small',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: 0,
          minHeight: 30,
        },
        sizeSmall: { padding: '4px 10px', minHeight: 30, fontSize: '0.8125rem' },
        sizeMedium: { padding: '6px 14px', minHeight: 34 },
      },
    },
    MuiIconButton: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          padding: 6,
          borderRadius: radius.md,
        },
        sizeSmall: { padding: 4, minWidth: 30, minHeight: 30 },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: radius.md },
        inputSizeSmall: { padding: '6px 10px', fontSize: '0.8125rem' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        sizeSmall: { fontSize: '0.8125rem' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        sizeSmall: { fontSize: '0.8125rem' },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: '0.72rem', marginLeft: 2, marginRight: 2, lineHeight: 1.35 },
      },
    },
    MuiCheckbox: { defaultProps: { size: 'small' } },
    MuiRadio: { defaultProps: { size: 'small' } },
    MuiSwitch: { defaultProps: { size: 'small' } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.75rem',
          padding: '4px 10px',
          letterSpacing: 0,
        },
        sizeSmall: { padding: '3px 8px' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          minHeight: 36,
          padding: '6px 12px',
          letterSpacing: 0,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 36 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.72rem',
          padding: '6px 10px',
          backgroundColor: ds.surface,
          color: ds.text,
          border: `1px solid ${ds.border}`,
          borderRadius: radius.md,
          boxShadow: 'var(--ds-shadow-2)',
        },
        arrow: {
          color: ds.surface,
          '&::before': {
            border: `1px solid ${ds.border}`,
            boxSizing: 'border-box',
          },
        },
      },
    },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${ds.border}`,
          borderRadius: radius.md,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 36,
          padding: '0 12px',
          backgroundColor: ds.surfaceSoft,
          '&.Mui-expanded': { minHeight: 36 },
        },
        content: {
          margin: '6px 0',
          fontSize: '0.8125rem',
          fontWeight: 600,
          '&.Mui-expanded': { margin: '6px 0' },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 10,
          borderTop: `1px solid ${ds.border}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: radius.lg },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '0.95rem',
          fontWeight: 700,
          padding: '12px 16px',
          borderBottom: `1px solid ${ds.border}`,
          backgroundColor: ds.surfaceSoft,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '14px 16px' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '10px 14px',
          borderTop: `1px solid ${ds.border}`,
          gap: 6,
          '& > :not(:first-of-type)': { marginLeft: 0 },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: ds.border },
        elevation1: { boxShadow: 'var(--ds-shadow-1)' },
        elevation2: { boxShadow: 'var(--ds-shadow-2)' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { borderBottom: 0 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.72rem' },
        sizeSmall: { height: 20, fontSize: '0.68rem' },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        sizeSmall: {
          minWidth: 30,
          height: 30,
          fontSize: '0.75rem',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: ds.border },
      },
    },
  },
  });
};

/** Default (light) theme — kept for any non-reactive import. */
export const theme = createAppTheme('light');

export default theme;
